import React, { useState, useEffect } from 'react';
import {
  Search,
  ChevronDown,
  ChevronUp,
  Trash2,
  ArrowRight,
  X,
  RefreshCw,
  User,
  Package,
  Calendar,
  Hash,
  Box,
  Move,
  Eye,
  AlertTriangle
} from 'lucide-react';
import { Button, Input, Card, Badge, LoadingSpinner, Notification, Select } from './SharedComponents';
import moment from 'moment';

const API_URL = import.meta.env.VITE_API_URL;

const ClientShelves = () => {
  // State management
  const [shelves, setShelves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [viewMode, setViewMode] = useState('client');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Transfer dialog states
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [transferShelfCode, setTransferShelfCode] = useState('');
  const [recentShelfCodes, setRecentShelfCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
  };

  useEffect(() => {
    fetchShelves();

    // Load recent shelf codes from localStorage
    const saved = localStorage.getItem('recentShelfCodes');
    if (saved) {
      setRecentShelfCodes(JSON.parse(saved));
    }
  }, []);

  const fetchShelves = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/get-shelving-data`);
      if (!response.ok) {
        throw new Error('Failed to fetch shelving data');
      }
      const data = await response.json();
      setShelves(data);
    } catch (err) {
      setError(err.message);
      showNotification('Failed to load shelving data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (key) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedItems(newExpanded);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Filter and sort data
  const filteredShelves = shelves.filter(shelf => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (shelf.name || '').toLowerCase().includes(searchLower) ||
      (shelf.FULL_SHELF_CODE || '').toLowerCase().includes(searchLower) ||
      (shelf.SKU || '').toLowerCase().includes(searchLower) ||
      (shelf.TITLE || '').toLowerCase().includes(searchLower)
    );
  });

  // Group data based on view mode
  const groupedData = () => {
    const grouped = {};

    filteredShelves.forEach(shelf => {
      const key = viewMode === 'client' ? (shelf.name || 'Unknown Client') : shelf.FULL_SHELF_CODE;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(shelf);
    });

    // Sort groups
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
      if (sortOrder === 'asc') {
        return a.localeCompare(b);
      } else {
        return b.localeCompare(a);
      }
    });

    // Sort items within each group
    sortedKeys.forEach(key => {
      grouped[key].sort((a, b) => {
        let aVal, bVal;
        switch (sortBy) {
          case 'name':
            aVal = a.name || '';
            bVal = b.name || '';
            break;
          case 'shelf':
            aVal = a.FULL_SHELF_CODE || '';
            bVal = b.FULL_SHELF_CODE || '';
            break;
          case 'sku':
            aVal = a.SKU || '';
            bVal = b.SKU || '';
            break;
          case 'qty':
            aVal = parseInt(a.QTY) || 0;
            bVal = parseInt(b.QTY) || 0;
            break;
          case 'updated':
            aVal = new Date(a.UPDATED || 0);
            bVal = new Date(b.UPDATED || 0);
            break;
          default:
            return 0;
        }

        if (sortBy === 'qty' || sortBy === 'updated') {
          return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        } else {
          return sortOrder === 'asc' ?
            aVal.toString().localeCompare(bVal.toString()) :
            bVal.toString().localeCompare(aVal.toString());
        }
      });
    });

    return { grouped, sortedKeys };
  };

  const { grouped, sortedKeys } = groupedData();

  // Save recent shelf codes to localStorage
  const saveRecentShelfCode = (shelfCode) => {
    const updated = [shelfCode, ...recentShelfCodes.filter(code => code !== shelfCode)].slice(0, 5);
    setRecentShelfCodes(updated);
    localStorage.setItem('recentShelfCodes', JSON.stringify(updated));
  };

  // Handle remove item
  const handleRemoveItem = async () => {
    if (!itemToDelete) return;

    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/update_shelving_qty`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key: itemToDelete.KEY,
          qty: 0, // Setting to 0 will delete the entry
          employee: 'System',
          sku: itemToDelete.SKU,
          shelfCode: itemToDelete.FULL_SHELF_CODE,
          shipmentId: 'MANUAL_REMOVAL',
          action_type: 'CLIENT_SHELVES_REMOVE'
        }),
      });

      if (response.ok) {
        await fetchShelves();
        showNotification('Item removed successfully');
        setShowDeleteConfirm(false);
        setItemToDelete(null);
      } else {
        throw new Error('Failed to remove item');
      }
    } catch (error) {
      console.error('Error removing item:', error);
      showNotification('Error removing item', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle transfer item
  const handleTransferDialog = (item) => {
    setSelectedItem(item);
    setTransferShelfCode('');
    setShowTransferDialog(true);
  };

  // Execute transfer
  const executeTransfer = async () => {
    if (!transferShelfCode.trim()) {
      showNotification('Please enter a shelf code', 'error');
      return;
    }

    try {
      setIsLoading(true);

      // First remove from current shelf
      await fetch(`${API_URL}/api/update_shelving_qty`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key: selectedItem.KEY,
          qty: 0,
          employee: 'System',
          sku: selectedItem.SKU,
          shelfCode: selectedItem.FULL_SHELF_CODE,
          shipmentId: 'MANUAL_TRANSFER',
          action_type: 'CLIENT_SHELVES_TRANSFER_OUT'
        }),
      });

      // Then add to new shelf
      const newKey = `${transferShelfCode.toUpperCase()}-${selectedItem.SKU}`;
      await fetch(`${API_URL}/api/add_or_update_shelving_qty`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key: newKey,
          qty: selectedItem.QTY,
          sku: selectedItem.SKU,
          shelfCode: transferShelfCode.toUpperCase(),
          employee: 'System',
          shipmentId: 'MANUAL_TRANSFER',
          action_type: 'CLIENT_SHELVES_TRANSFER_IN',
          team_id: selectedItem.TEAM_ID
        }),
      });

      // Save to recent shelf codes
      saveRecentShelfCode(transferShelfCode.toUpperCase());
      await fetchShelves();

      setShowTransferDialog(false);
      showNotification(`Transferred ${selectedItem.QTY} units to shelf ${transferShelfCode.toUpperCase()}`);
    } catch (error) {
      console.error('Error transferring item:', error);
      showNotification('Error transferring item', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Notification */}
      <Notification
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ show: false, message: '', type: 'success' })}
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Client Shelves</h1>
            <p className="text-gray-600">Manage inventory placement across warehouse shelves by client or location</p>
          </div>
          <Button
            onClick={fetchShelves}
            variant="outline"
            disabled={loading}
            className="flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Controls */}
        <Card className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4 mb-4">
            {/* Search */}
            <div className="flex-1">
              <Input
                placeholder="Search clients, shelves, SKUs, or products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={Search}
                className="text-base"
              />
            </div>

            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('client')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'client'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <User className="w-4 h-4 mr-2 inline" />
                By Client
              </button>
              <button
                onClick={() => setViewMode('shelf')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'shelf'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Box className="w-4 h-4 mr-2 inline" />
                By Shelf
              </button>
            </div>
          </div>

          {/* Sort Controls */}
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="text-gray-600 font-medium">Sort by:</span>
            {[
              { key: 'name', label: 'Client', icon: User },
              { key: 'shelf', label: 'Shelf', icon: Box },
              { key: 'sku', label: 'SKU', icon: Hash },
              { key: 'qty', label: 'Quantity', icon: Package },
              { key: 'updated', label: 'Updated', icon: Calendar }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => handleSort(key)}
                className={`px-3 py-1.5 rounded-lg transition-colors flex items-center ${
                  sortBy === key
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <Icon className="w-3 h-3 mr-1" />
                {label}
                {sortBy === key && (
                  sortOrder === 'asc' ?
                    <ChevronUp className="w-3 h-3 ml-1" /> :
                    <ChevronDown className="w-3 h-3 ml-1" />
                )}
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" className="mb-4" />
          <div className="ml-4">
            <h3 className="text-lg font-medium text-gray-900">Loading shelf data...</h3>
            <p className="text-gray-500">Please wait while we fetch the latest inventory information.</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card className="p-6">
          <div className="flex items-center">
            <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">Error Loading Data</h3>
              <p className="text-gray-500">{error}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Results */}
      {!loading && !error && (
        <>
          {sortedKeys.length === 0 ? (
            <div className="text-center py-20">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Shelves Found</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                {searchTerm
                  ? `No shelves match your search for "${searchTerm}". Try different keywords.`
                  : "No shelf inventory data available."}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedKeys.map((groupKey) => {
                const items = grouped[groupKey];
                const isExpanded = expandedItems.has(groupKey);
                const totalQty = items.reduce((sum, item) => sum + (parseInt(item.QTY) || 0), 0);

                return (
                  <Card key={groupKey} className="overflow-hidden">
                    {/* Group Header */}
                    <div
                      className="px-6 py-4 bg-gray-50 border-b cursor-pointer hover:bg-gray-100 transition-colors flex items-center justify-between"
                      onClick={() => toggleExpanded(groupKey)}
                    >
                      <div className="flex items-center space-x-4">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronUp className="w-5 h-5 text-gray-400 transform rotate-180" />
                        )}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{groupKey}</h3>
                          <p className="text-sm text-gray-500">
                            {items.length} item{items.length !== 1 ? 's' : ''} • Total: {totalQty.toLocaleString()} units
                          </p>
                        </div>
                      </div>
                      <Badge variant="default">
                        {items.length}
                      </Badge>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Product Details
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {viewMode === 'client' ? 'Shelf Location' : 'Client'}
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                SKU
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Quantity
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Last Updated
                              </th>
                              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {items.map((item, index) => (
                              <tr key={`${item.KEY}-${index}`} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                  <div className="flex items-start space-x-3">
                                    {item.SMALL_IMG ? (
                                      <img
                                        src={item.SMALL_IMG}
                                        alt={item.TITLE || item.SKU}
                                        className="w-12 h-12 object-contain rounded border border-gray-200 flex-shrink-0 bg-gray-50"
                                      />
                                    ) : (
                                      <div className="w-12 h-12 bg-gray-100 rounded border border-gray-200 flex items-center justify-center flex-shrink-0">
                                        <Package className="w-6 h-6 text-gray-400" />
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 line-clamp-2">
                                        {item.TITLE || 'Untitled Product'}
                                      </p>
                                      {item.ASIN && (
                                        <p className="text-xs text-gray-500 mt-1">ASIN: {item.ASIN}</p>
                                      )}
                                    </div>
                                  </div>
                                </td>

                                <td className="px-6 py-4">
                                  {viewMode === 'client' ? (
                                    <div className="flex items-center">
                                      <Box className="w-4 h-4 mr-2 text-gray-400" />
                                      <code className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded font-mono">
                                        {item.FULL_SHELF_CODE}
                                      </code>
                                    </div>
                                  ) : (
                                    <div className="flex items-center">
                                      <User className="w-4 h-4 mr-2 text-gray-400" />
                                      <span className="text-sm text-gray-900">{item.name || 'Unknown Client'}</span>
                                    </div>
                                  )}
                                </td>

                                <td className="px-6 py-4">
                                  <div className="flex items-center">
                                    <Hash className="w-4 h-4 mr-2 text-gray-400" />
                                    <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                                      {item.SKU}
                                    </code>
                                  </div>
                                </td>

                                <td className="px-6 py-4">
                                  <Badge variant="info" className="font-mono">
                                    {item.QTY}
                                  </Badge>
                                </td>

                                <td className="px-6 py-4">
                                  <div className="flex items-center text-sm text-gray-500">
                                    <Calendar className="w-4 h-4 mr-2" />
                                    <div>
                                      <div className="text-sm text-gray-900">
                                        {item.UPDATED ? moment(item.UPDATED).format('MMM D, YYYY') : 'N/A'}
                                      </div>
                                      {item.UPDATED && (
                                        <div className="text-xs text-gray-500">
                                          {moment(item.UPDATED).fromNow()}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>

                                <td className="px-6 py-4">
                                  <div className="flex items-center justify-center space-x-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleTransferDialog(item)}
                                      disabled={isLoading}
                                      className="p-2"
                                      title="Transfer to another shelf"
                                    >
                                      <Move className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setItemToDelete(item);
                                        setShowDeleteConfirm(true);
                                      }}
                                      disabled={isLoading}
                                      className="p-2 text-red-600 hover:text-red-700"
                                      title="Remove from shelf"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {/* Summary */}
          {!loading && !error && filteredShelves.length > 0 && (
            <Card className="p-4">
              <div className="flex justify-between items-center text-sm text-gray-600">
                <span>
                  Showing {filteredShelves.length.toLocaleString()} item{filteredShelves.length !== 1 ? 's' : ''}
                  {searchTerm && ` matching "${searchTerm}"`}
                </span>
                <span>
                  Total Quantity: {filteredShelves.reduce((sum, item) => sum + (parseInt(item.QTY) || 0), 0).toLocaleString()} units
                </span>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Transfer Dialog */}
      {showTransferDialog && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Transfer Item</h3>
                <Button
                  variant="ghost"
                  onClick={() => setShowTransferDialog(false)}
                  className="p-2"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Item Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  {selectedItem.SMALL_IMG ? (
                    <img
                      src={selectedItem.SMALL_IMG}
                      alt={selectedItem.TITLE || selectedItem.SKU}
                      className="w-12 h-12 object-contain bg-white rounded border border-gray-200"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                      <Package className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 line-clamp-2">
                      {selectedItem.TITLE || 'Untitled Product'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {selectedItem.SKU} • {selectedItem.QTY} units
                    </p>
                    <p className="text-xs text-gray-500">
                      Current shelf: {selectedItem.FULL_SHELF_CODE}
                    </p>
                  </div>
                </div>
              </div>

              {/* New Shelf Code Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Shelf Code
                </label>
                <Input
                  value={transferShelfCode}
                  onChange={(e) => setTransferShelfCode(e.target.value.toUpperCase())}
                  placeholder="e.g., P-01-02"
                />
              </div>

              {/* Recent Shelf Codes */}
              {recentShelfCodes.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Recent Shelf Codes:</p>
                  <div className="flex flex-wrap gap-2">
                    {recentShelfCodes.slice(0, 4).map((code, index) => (
                      <button
                        key={index}
                        onClick={() => setTransferShelfCode(code)}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                      >
                        {code}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowTransferDialog(false)}
                  disabled={isLoading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={executeTransfer}
                  disabled={isLoading || !transferShelfCode.trim()}
                  className="flex-1 flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Transferring...
                    </>
                  ) : (
                    <>
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Transfer
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Remove Item from Shelf</h3>
              </div>

              <p className="text-gray-600 mb-4">
                Are you sure you want to remove this item from the shelf?
              </p>

              <div className="bg-gray-50 p-3 rounded-lg mb-6">
                <div className="flex items-center space-x-3">
                  {itemToDelete.SMALL_IMG ? (
                    <img
                      src={itemToDelete.SMALL_IMG}
                      alt={itemToDelete.TITLE || itemToDelete.SKU}
                      className="w-8 h-8 object-contain bg-white rounded"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                      <Package className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {itemToDelete.TITLE || 'Untitled Product'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {itemToDelete.SKU} • {itemToDelete.QTY} units • Shelf: {itemToDelete.FULL_SHELF_CODE}
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-500 mb-6">
                This action cannot be undone.
              </p>

              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setItemToDelete(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRemoveItem}
                  disabled={isLoading}
                  variant="danger"
                  className="flex-1"
                >
                  {isLoading ? 'Removing...' : 'Remove Item'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ClientShelves;